import { api, ApiError } from "@/api/api";
import { ImageUploadResponseData } from "@/api/images-types";
import { MaskWithImage } from "@/api/masks-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

type UploadManualImageParams = {
  imageId: string;
  episodeId?: string;
  imageFile: File;
  maskFile?: File;
};

type UploadManualMaskParams = {
  imageId: string;
  maskFile: File;
};

export function useUploadManualImage() {
  const queryClient = useQueryClient();

  return useMutation<
    ImageUploadResponseData,
    ApiError,
    UploadManualImageParams
  >({
    mutationKey: ["POST", "images", "image-upload"],
    mutationFn: async ({ imageId, imageFile, maskFile }) => {
      const imageFormData = new FormData();
      imageFormData.append("file", imageFile);

      const imageResult = await api.images.imageId.imageUpload.post({
        imageId,
        formData: imageFormData,
      });

      // Upload mask if provided, using the newly created image ID
      if (maskFile) {
        if (
          !imageResult.new_image ||
          imageResult.new_image.images.length === 0
        ) {
          throw new Error(
            "No new image was created in the image upload response",
          );
        }

        const maskFormData = new FormData();
        maskFormData.append("file", maskFile);
        await api.images.imageId.maskUpload.post({
          imageId,
          formData: maskFormData,
        });
      }

      return imageResult;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["GET", "images", variables.imageId],
      });
      queryClient.invalidateQueries({
        queryKey: ["GET", "episodeDetails", variables.episodeId || ""],
      });
      queryClient.invalidateQueries({ queryKey: ["GET", "episodes"] });
      queryClient.invalidateQueries({ queryKey: ["GET", "masks"] });
      queryClient.invalidateQueries({
        queryKey: ["GET", "images", "needingManualEdit"],
      });
    },
  });
}

export function useUploadManualMask() {
  const queryClient = useQueryClient();

  return useMutation<MaskWithImage, ApiError, UploadManualMaskParams>({
    mutationKey: ["POST", "images", "mask-upload"],
    mutationFn: async ({ imageId, maskFile }) => {
      const maskFormData = new FormData();
      maskFormData.append("file", maskFile);
      const maskResult = await api.images.imageId.maskUpload.post({
        imageId,
        formData: maskFormData,
      });

      return maskResult;
    },
    onSuccess: (_) => {
      // TODO: make invalidations more granular by including IDs of affected entities
      queryClient.invalidateQueries({ queryKey: ["GET", "images"] });
      queryClient.invalidateQueries({ queryKey: ["GET", "episodes"] });
      queryClient.invalidateQueries({ queryKey: ["GET", "masks"] });
      queryClient.invalidateQueries({
        queryKey: ["GET", "images", "needingManualEdit"],
      });
    },
  });
}
